import { useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { identifyUser, resetUser, captureEvent } from "@/lib/analytics";
import { EVENTS } from "@/lib/analytics-events";

export function useUserSync() {
  const { user, session } = useAuth();

  useEffect(() => {
    if (user && session) {
      identifyUser(user.id, {
        email: user.email,
        created_at: user.created_at,
      });
      captureEvent(EVENTS.AUTH.SIGNED_IN, { method: "email" });
    } else {
      resetUser();
    }
  }, [user, session]);

  const syncCVToSupabase = useCallback(
    async (cvData: Record<string, unknown>, templateName: string) => {
      if (!user) return;

      try {
        const response = await fetch("/api/user/cv-sync", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
            cvData: cvData,
            templateName: templateName,
          }),
        });
        return response.json();
      } catch (error) {
        console.error("Failed to sync CV to Supabase:", error);
      }
    },
    [user]
  );

  const loadCVFromSupabase = useCallback(
    async (templateName?: string) => {
      if (!user) return null;

      try {
        const response = await fetch(
          `/api/user/cv-sync?userId=${user.id}${templateName ? `&templateName=${templateName}` : ""}`
        );
        return response.json();
      } catch (error) {
        console.error("Failed to load CV from Supabase:", error);
        return null;
      }
    },
    [user]
  );

  return {
    isLoggedIn: !!user,
    userId: user?.id,
    syncCVToSupabase,
    loadCVFromSupabase,
  };
}
